/**
 * Service worker registration + update lifecycle.
 *
 * The actual SW source is `client/src/sw.ts`, compiled to `/sw.js`
 * by vite-plugin-pwa during `npm run build`. main.tsx only calls
 * `registerServiceWorker()` from a PROD build (Vite dev's HMR plus a
 * long-lived SW caching index.html breaks boot after entry-point
 * changes).
 *
 * History note: the previous version of this file aggressively
 * unregistered any existing service workers and cleared every cache
 * on every load because the SPA briefly had no working SW to replace
 * the old (broken) one. Now that vite-plugin-pwa emits a real
 * `/sw.js`, the browser handles the install / activate / replace
 * lifecycle correctly and the nuclear cleanup would break offline
 * support. If a legacy broken SW is ever stuck on someone's machine,
 * they can clear it via Chrome DevTools → Application → Service
 * Workers → Unregister — the standard escape hatch.
 */

import { isStandalone } from "./standalone";
import { getPendingCount } from "./offline-sync";
import { updateAvailable } from "./update-gate";

export function registerServiceWorker() {
    if (!("serviceWorker" in navigator)) return;

    window.addEventListener("load", async () => {
        try {
            // Probe before registering so a 404 (e.g. the SW file isn't
            // included in this deploy) doesn't surface as a noisy
            // browser-console exception. Belt-and-suspenders since we
            // only register from PROD builds.
            const probe = await fetch("/sw.js", { method: "HEAD", cache: "no-store" });
            const contentType = probe.headers.get("content-type") ?? "";
            if (!probe.ok || !contentType.includes("javascript")) {
                console.warn(`[sw] /sw.js probe returned status=${probe.status} ct=${contentType}; not registering.`);
                return;
            }

            const reg = await navigator.serviceWorker.register("/sw.js");
            console.log("[sw] registered:", reg.scope);

            // Pull any waiting build immediately — especially important
            // for iPad home-screen PWAs that force-quit between visits.
            void reg.update();

            // On standalone (installed PWA), auto-adopt a worker that was
            // ALREADY waiting at launch — the UI hasn't rendered yet so the
            // controllerchange reload is invisible.
            if (isStandalone() && reg.waiting) {
                reg.waiting.postMessage({ type: "SKIP_WAITING" });
            }

            // TECH-JOB-SCREEN §8 item 1 — when a fresh build finishes
            // installing, route it through the update gate: clean form
            // state applies it SILENTLY (SKIP_WAITING → controllerchange
            // reload below); dirty form state defers it behind the sticky
            // "Update ready — applies after you save" banner, and the
            // gate self-applies at the next safe moment (workflow save,
            // navigation to the list). No tech runs a stale bundle for
            // days because they never tapped "Reload" mid-job.
            const routeThroughGate = (worker: ServiceWorker) => {
                const outcome = updateAvailable(() => worker.postMessage({ type: "SKIP_WAITING" }));
                console.log(
                    `[sw] new build ${outcome === "applied" ? "applied silently" : "deferred (dirty form state)"}`,
                );
            };
            // A worker still INSTALLING: wait for it to reach `installed`,
            // then gate it. statechange is the right hook here.
            const announceUpdate = (worker: ServiceWorker) => {
                worker.addEventListener("statechange", () => {
                    if (worker.state === "installed" && navigator.serviceWorker.controller) {
                        routeThroughGate(worker);
                    }
                });
            };
            // #683 — a worker ALREADY WAITING at page load used to be handed
            // to announceUpdate too, which only attaches a statechange
            // listener. An already-waiting worker is already PAST the
            // installed transition, so that listener could never fire: dead
            // code for this exact case. The update therefore never reached the
            // gate, never applied silently, and the banner's 30s poll then saw
            // `reg.waiting` and nagged forever — the stuck gold strip Corbin
            // hit on desktop, which no amount of hard-refreshing could clear
            // (a waiting worker survives reloads by design; it only activates
            // when every tab releases the old one, and a second Groundwork tab
            // pins it).
            //
            // Route it through the gate immediately instead.
            if (reg.waiting) routeThroughGate(reg.waiting);
            reg.addEventListener("updatefound", () => {
                const newWorker = reg.installing;
                if (newWorker) announceUpdate(newWorker);
            });

            // §8: version-check on app focus + every 5 minutes (was hourly)
            // — a truck that parked overnight picks up the new build within
            // minutes of the first tap, not the first hour.
            const UPDATE_INTERVAL_MS = 5 * 60 * 1000;
            setInterval(() => void reg.update().catch(() => undefined), UPDATE_INTERVAL_MS);
            const checkOnForeground = () => void reg.update().catch(() => undefined);
            window.addEventListener("focus", checkOnForeground);
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") checkOnForeground();
            });

            // Listen for push-initiated navigation requests from the
            // SW (sw.ts → notificationclick → postMessage). The SW
            // can't directly steer the SPA router, so it asks the page
            // to do it instead. Using location for now — the SPA's
            // BrowserRouter picks it up on the next render. If we
            // outgrow this, swap for a navigate() call exposed by a
            // context provider.
            navigator.serviceWorker.addEventListener("message", (event) => {
                const data = event.data as { type?: string; link?: string } | null;
                if (data?.type === "PUSH_NAVIGATE" && typeof data.link === "string") {
                    if (window.location.pathname + window.location.search !== data.link) {
                        window.location.assign(data.link);
                    }
                }
            });

            // When a freshly-activated SW takes control mid-session,
            // a hard reload pulls the new HTML + bundles so we don't
            // hand the user a half-old / half-new UI.
            let reloading = false;
            navigator.serviceWorker.addEventListener("controllerchange", () => {
                if (reloading) return;
                reloading = true;
                window.location.reload();
            });
        } catch (err) {
            console.error("[sw] registration failed:", err);
        }
    });
}

/** How long to give a waiting worker to actually leave `waiting` after
 *  SKIP_WAITING before we stop trusting it and force the escape hatch. */
export const SKIP_WAITING_TIMEOUT_MS = 3_000;

/**
 * Adopt the installed-but-waiting worker. Called from UpdateBanner when the
 * user taps the strip.
 *
 * #683 — this used to be fire-and-forget: post SKIP_WAITING and hope. If the
 * waiting worker never left `waiting` — the message was lost, or the waiting
 * script predates the SKIP_WAITING handler and simply has no listener for it
 * — then nothing happened. No reload, no feedback, no recovery: the tap was a
 * dead button, which is exactly what Corbin reported.
 *
 * Now **every branch ends in something the user can see**:
 *   · worker leaves `waiting` → `controllerchange` reloads the page;
 *   · it doesn't, within the timeout → unregister the stuck registration and
 *     reload. The fresh page re-registers a clean SW from the network. That's
 *     the documented DevTools escape hatch (Application → Service Workers →
 *     Unregister), automated so a tech never needs it;
 *   · no waiting worker at all → plain reload for the freshest assets.
 *
 * `timeoutMs` is injectable so the timeout path is testable without a wait.
 */
export async function applyServiceWorkerUpdate(timeoutMs: number = SKIP_WAITING_TIMEOUT_MS): Promise<void> {
    if (!("serviceWorker" in navigator)) return;
    let reg: ServiceWorkerRegistration | undefined;
    try {
        reg = await navigator.serviceWorker.getRegistration();
    } catch {
        window.location.reload();
        return;
    }
    const waiting = reg?.waiting;
    if (!waiting) {
        window.location.reload();
        return;
    }

    await new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
        };

        // The happy path: the worker activates, `controllerchange` fires, and
        // the listener installed at registration reloads the page. We still
        // resolve so the caller can drop its "Updating…" state if the reload
        // is somehow suppressed.
        const onStateChange = () => {
            if (waiting.state !== "installed") {
                waiting.removeEventListener("statechange", onStateChange);
                finish();
            }
        };
        waiting.addEventListener("statechange", onStateChange);

        setTimeout(() => {
            waiting.removeEventListener("statechange", onStateChange);
            if (settled) return;
            // Still parked in `waiting` — the worker isn't going to adopt.
            // Unregister and reload; the fresh page pulls a clean SW.
            void (async () => {
                try {
                    await reg?.unregister();
                } catch {
                    // Even a failed unregister must not swallow the reload.
                }
                finish();
                window.location.reload();
            })();
        }, timeoutMs);

        waiting.postMessage({ type: "SKIP_WAITING" });
    });
}

export function requestNotificationPermission(): Promise<NotificationPermission> {
    if (!("Notification" in window)) return Promise.resolve("denied" as NotificationPermission);
    return Notification.requestPermission();
}

export async function getSyncQueueCount(): Promise<number> {
    // Delegate to offline-sync's hardened opener instead of opening the
    // DB here. A bare `indexedDB.open("ironclad-sync", 3)` with no
    // `onupgradeneeded` handler (as this used to do) creates the database
    // at v3 with NO object stores when it runs first — e.g. the offline
    // indicator polls this on app load before anything has written to the
    // queue. Every later open at the same version then skips the upgrade
    // and finds no `queue` store, so `transaction("queue")` throws
    // NotFoundError. Routing through `getPendingCount` guarantees the
    // schema is created/verified before any transaction runs.
    return getPendingCount();
}
